'use client'

import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Mic, Check, Download, Shield, Zap, Volume2 } from 'lucide-react';
import { useEffect, useState } from "react";
import { BsBrowserFirefox } from "react-icons/bs";
import { FaChrome, FaEdge } from "react-icons/fa";

export default function ExtensionsPage() {
  const [animateFeature, setAnimateFeature] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimateFeature(prev => (prev + 1) % 5);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Volume2 className="h-8 w-8 text-blue-400" />,
      title: "Background Noise Removal",
      description: "Eliminate unwanted background noise during your calls and recordings"
    },
    {
      icon: <Zap className="h-8 w-8 text-blue-400" />,
      title: "Filler Word Elimination",
      description: "Automatically remove um's, uh's, and other filler words from your speech"
    },
    {
      icon: <Mic className="h-8 w-8 text-blue-400" />,
      title: "Human-like Text-to-Speech",
      description: "Convert text to natural-sounding speech in real-time"
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-400" />,
      title: "Privacy Protection",
      description: "Your audio stays on your device with end-to-end encryption"
    },
    {
      icon: <Check className="h-8 w-8 text-blue-400" />,
      title: "AI Communication Coach",
      description: "Get real-time feedback and suggestions to improve your communication"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <main>
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center">
          <div className="container mx-auto px-4 py-32 text-center">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-tight mb-6 [text-wrap:balance]" style={{ fontFamily: 'Inria Serif, serif' }}>
              Enhance your communication
              <br />
              <span className="text-blue-400">with a single click</span>
            </h1>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto">
              Our browser extension brings AI-powered speech enhancement to all your favorite platforms
            </p>
            <Button 
              className="inline-flex items-center justify-center px-8 py-6 text-lg rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]"
              onClick={() => window.open("#", "_blank")}
            >
              <Download className="mr-2 h-5 w-5" />
              Get the Extension
            </Button>
            
            <div className="flex items-center justify-center space-x-6 mt-8">
              <div className="flex items-center text-gray-400">
                <FaChrome className="h-6 w-6 mr-2" />
                <span>Chrome</span>
              </div>
              <div className="flex items-center text-gray-400">
                <BsBrowserFirefox className="h-6 w-6 mr-2" />
                <span>Firefox</span>
              </div>
              <div className="flex items-center text-gray-400">
                <FaEdge className="h-6 w-6 mr-2" />
                <span>Edge</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-normal mb-16 text-center" style={{ fontFamily: 'Inria Serif, serif' }}>
              All-in-one speech enhancement suite
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className={`p-8 rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-black transition-all duration-500 ${
                    animateFeature === index ? 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.3)]' : ''
                  }`}
                >
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-2xl mb-3" style={{ fontFamily: 'Inria Serif, serif' }}>{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-normal mb-16 text-center" style={{ fontFamily: 'Inria Serif, serif' }}>
              How it works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">1</span>
                </div>
                <h3 className="text-2xl mb-3" style={{ fontFamily: 'Inria Serif, serif' }}>Install the extension</h3>
                <p className="text-gray-400">Download and add our extension to your favorite browser</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">2</span>
                </div>
                <h3 className="text-2xl mb-3" style={{ fontFamily: 'Inria Serif, serif' }}>Configure your settings</h3>
                <p className="text-gray-400">Customize noise removal, filler word settings and more</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">3</span>
                </div>
                <h3 className="text-2xl mb-3" style={{ fontFamily: 'Inria Serif, serif' }}>Start speaking clearly</h3>
                <p className="text-gray-400">Enjoy enhanced communication on any platform</p>
              </div>
            </div>
          </div>
        </section>

        {/* Compatibility Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-normal mb-6 text-center" style={{ fontFamily: 'Inria Serif, serif' }}>
              Works everywhere you do
            </h2>
            <p className="text-xl text-gray-400 mb-16 text-center max-w-3xl mx-auto">
              Our extension seamlessly integrates with all major communication platforms
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {['Zoom', 'Google Meet', 'Microsoft Teams', 'Discord', 'Slack', 'Webex', 'Skype', 'Any Web App'].map((platform, index) => (
                <div key={index} className="p-6 rounded-xl border border-gray-800 text-center hover:border-blue-500 transition-colors">
                  <p className="text-lg">{platform}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-normal mb-16 text-center" style={{ fontFamily: 'Inria Serif, serif' }}>
              What users are saying
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  quote: "This extension has completely transformed my online meetings. No more awkward ums and background noise!",
                  author: "Sarah K., Marketing Director"
                },
                {
                  quote: "The AI-powered speech enhancement is incredible. My colleagues have noticed the difference in my presentations.",
                  author: "Michael T., Product Manager"
                },
                {
                  quote: "As someone who works from home with kids, the background noise removal is a game changer for my calls.",
                  author: "Jennifer R., Sales Executive"
                }
              ].map((testimonial, index) => (
                <div key={index} className="p-8 rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-black">
                  <svg className="h-8 w-8 text-blue-400 mb-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <p className="text-lg mb-6">{testimonial.quote}</p>
                  <p className="text-gray-400">{testimonial.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto p-12 rounded-3xl border border-gray-800 bg-gradient-to-br from-blue-900/20 to-black">
              <h2 className="text-4xl md:text-5xl font-normal mb-6" style={{ fontFamily: 'Inria Serif, serif' }}>
                Ready to speak clearly?
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Join thousands of professionals enhancing their communication today
              </p>
              <Button 
                className="inline-flex items-center justify-center px-8 py-6 text-lg rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-[0_0_15px_rgba(59,130,246,0.5)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]"
                onClick={() => window.open("#", "_blank")}
              >
                <Download className="mr-2 h-5 w-5" />
                Get the Extension Now
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-normal mb-16 text-center" style={{ fontFamily: 'Inria Serif, serif' }}>
              Frequently Asked Questions
            </h2>
            
            <div className="max-w-3xl mx-auto space-y-8">
              {[
                {
                  question: "Is SpeakclearlyAI extension free to use?",
                  answer: "We offer both free and premium versions. The free version includes basic noise removal, while the premium version unlocks all features including filler word removal and advanced AI suggestions."
                },
                {
                  question: "How does the extension process my audio?",
                  answer: "All audio processing happens locally on your device. We use edge AI technology to ensure your voice data never leaves your computer, providing maximum privacy and security."
                },
                {
                  question: "Which browsers are supported?",
                  answer: "Our extension is compatible with Chrome, Firefox, and Microsoft Edge. Support for additional browsers is coming soon."
                },
                {
                  question: "Will this work with any web-based meeting platform?",
                  answer: "Yes! Our extension works with all major web-based communication platforms including Zoom, Google Meet, Microsoft Teams, and more."
                },
                {
                  question: "Does the extension affect my system performance?",
                  answer: "The extension is designed to be lightweight and efficient. It uses minimal CPU resources, ensuring smooth performance even during long meetings."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 rounded-xl border border-gray-800 hover:border-blue-500 transition-colors">
                  <h3 className="text-xl mb-3" style={{ fontFamily: 'Inria Serif, serif' }}>{faq.question}</h3>
                  <p className="text-gray-400">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="py-20 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center space-y-8">
            <div className="flex items-center space-x-2">
              <Mic className="h-6 w-6" />
              <span className="text-xl font-semibold">SpeakclearlyAI</span>
            </div>
            <a href="mailto:team@speakclearly.xyz" className="text-gray-400 hover:text-white transition-colors">
              team@speakclearly.xyz
            </a>
            <div className="flex flex-wrap justify-center gap-8">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/products" className="text-gray-400 hover:text-white transition-colors">
                Products
              </Link>
              <Link to="/extensions" className="text-gray-400 hover:text-white transition-colors">
                Extensions
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/privacy_policy.html" className="text-gray-400 hover:text-white transition-colors">
                Privacy policy
              </Link>
            </div>
            <a 
              href="https://www.producthunt.com/posts/speakclearlyai?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-speakclearlyai" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <img 
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=833048&theme=light&t=1738218482436" 
                alt="SpeakclearlyAI - AI-POWERED FILLER WORD REMOVAL & BACKGROUND NOISE CLEANUP | Product Hunt" 
                style={{ width: "250px", height: "54px" }} 
                width="250" 
                height="54" 
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
